import { generateClient } from 'aws-amplify/data';
import logger from '../utils/logger.js';
import { getCurrentUser } from 'aws-amplify/auth';
import {
  createNetworkError,
  createValidationError,
  isErrorResponse,
  logError
} from '../utils/StandardErrorFormat.js';

/**
 * UserPreferencesService - Manages user preferences via GraphQL
 *
 * Replaces localStorage-based preferences with backend persistence
 * Handles sort preferences, UI preferences, AI settings, and notification settings
 */
class UserPreferencesService {
  constructor() {
    // Create service-specific logger
    this.logger = logger.child('UserPreferencesService');

    this.client = generateClient();
    this.cache = null;
    this.cacheTimestamp = null;
    this.cacheDuration = 300000; // 5 minutes cache
  }

  /**
   * Get current user ID
   * @returns {Promise<string>} User ID
   */
  async getCurrentUserId() {
    try {
      const user = await getCurrentUser();
      return user.userId;
    } catch (error) {
      this.logger.error('Error getting current user:', error);
      return createValidationError('User not authenticated', error);
    }
  }

  /**
   * Get or create user preferences
   * @returns {Promise<Object>} User preferences
   */
  async getPreferences() {
    try {
      // Check cache first
      if (this.cache && this.cacheTimestamp && (Date.now() - this.cacheTimestamp < this.cacheDuration)) {
        return this.cache;
      }

      const userId = await this.getCurrentUserId();

      // Try to get existing preferences
      const { data: existingPrefs } = await this.client.models.UserPreferences.list({
        filter: {
          userId: { eq: userId }
        }
      });

      let prefs;
      if (existingPrefs && existingPrefs.length > 0) {
        prefs = existingPrefs[0];
      } else {
        // Create default preferences
        const createResult = await this.client.models.UserPreferences.create({
          userId: userId,
          sortPreferences: JSON.stringify({
            customContentSortBy: 'updatedAt',
            customContentSortOrder: 'desc',
            templateSortBy: 'name',
            templateSortOrder: 'asc'
          }),
          uiPreferences: JSON.stringify({
            theme: 'light',
            viewMode: 'grid',
            sidebarCollapsed: false
          }),
          aiSettings: JSON.stringify({
            style: 'professional',
            tone: 'professional',
            complexityLevel: 'intermediate'
          }),
          emailNotifications: true,
          pushNotifications: false,
          enabledFeatures: [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });

        // Amplify can return { data: null, errors: [...] } on mutation
        // failure — surface that instead of letting it cascade into a
        // null-deref TypeError below.
        if (createResult?.errors?.length) {
          this.logger.warn('UserPreferences create returned GraphQL errors', {
            errors: createResult.errors
          });
        }
        prefs = createResult?.data || null;
      }

      // If we still don't have a prefs row (auth issue, schema mismatch,
      // etc.), fall through to defaults without throwing.
      if (!prefs) {
        return this.getDefaultPreferences();
      }

      // Parse JSON fields
      const parsed = {
        ...prefs,
        sortPreferences: prefs.sortPreferences ? JSON.parse(prefs.sortPreferences) : {},
        uiPreferences: prefs.uiPreferences ? JSON.parse(prefs.uiPreferences) : {},
        aiSettings: prefs.aiSettings ? JSON.parse(prefs.aiSettings) : {}
      };

      // Cache the result
      this.cache = parsed;
      this.cacheTimestamp = Date.now();

      return parsed;
    } catch (error) {
      this.logger.error('Error getting user preferences:', error);
      return this.getDefaultPreferences();
    }
  }

  /**
   * Default preferences returned when the row can't be read or created.
   * Keeps callers insulated from GraphQL/auth failures so the UI still loads.
   */
  getDefaultPreferences() {
    return {
      sortPreferences: {
        customContentSortBy: 'updatedAt',
        customContentSortOrder: 'desc'
      },
      uiPreferences: {
        theme: 'light',
        viewMode: 'grid'
      },
      aiSettings: {
        style: 'professional',
        tone: 'professional'
      },
      emailNotifications: true,
      pushNotifications: false,
      enabledFeatures: []
    };
  }

  /**
   * Update sort preferences
   * @param {Object} sortPrefs - Sort preferences to update
   * @returns {Promise<Object>} Updated preferences
   */
  async updateSortPreferences(sortPrefs) {
    try {
      const prefs = await this.getPreferences();
      if (!prefs?.id) {
        throw new Error('User preferences not initialized — upstream create failed; cannot update sort preferences');
      }
      const currentSort = prefs.sortPreferences || {};
      const updatedSort = { ...currentSort, ...sortPrefs };

      const updateResult = await this.client.models.UserPreferences.update({
        id: prefs.id,
        sortPreferences: JSON.stringify(updatedSort),
        updatedAt: new Date().toISOString()
      });
      const { data: updated, errors } = updateResult || {};

      if (!updated) {
        throw new Error(
          'UserPreferences.update returned null' +
          (errors?.length ? `: ${errors.map(e => e.message).join('; ')}` : '')
        );
      }

      // Clear cache
      this.clearCache();

      return {
        ...updated,
        sortPreferences: JSON.parse(updated.sortPreferences)
      };
    } catch (error) {
      this.logger.error('Error updating sort preferences:', error);
      return createNetworkError('Failed to update sort preferences', error);
    }
  }

  /**
   * Update UI preferences
   * @param {Object} uiPrefs - UI preferences to update
   * @returns {Promise<Object>} Updated preferences
   */
  async updateUIPreferences(uiPrefs) {
    try {
      const prefs = await this.getPreferences();
      if (!prefs?.id) {
        throw new Error('User preferences not initialized — upstream create failed; cannot update UI preferences');
      }
      const currentUI = prefs.uiPreferences || {};
      const updatedUI = { ...currentUI, ...uiPrefs };

      const updateResult = await this.client.models.UserPreferences.update({
        id: prefs.id,
        uiPreferences: JSON.stringify(updatedUI),
        updatedAt: new Date().toISOString()
      });
      const { data: updated, errors } = updateResult || {};

      if (!updated) {
        throw new Error(
          'UserPreferences.update returned null' +
          (errors?.length ? `: ${errors.map(e => e.message).join('; ')}` : '')
        );
      }

      // Clear cache
      this.clearCache();

      return {
        ...updated,
        uiPreferences: JSON.parse(updated.uiPreferences)
      };
    } catch (error) {
      this.logger.error('Error updating UI preferences:', error);
      return createNetworkError('Failed to update UI preferences', error);
    }
  }

  /**
   * Update AI settings
   * @param {Object} aiSettings - AI settings to update
   * @returns {Promise<Object>} Updated preferences
   */
  async updateAISettings(aiSettings) {
    try {
      const prefs = await this.getPreferences();
      if (!prefs?.id) {
        throw new Error('User preferences not initialized — upstream create failed; cannot update AI settings');
      }
      const currentAI = prefs.aiSettings || {};
      const updatedAI = { ...currentAI, ...aiSettings };

      const updateResult = await this.client.models.UserPreferences.update({
        id: prefs.id,
        aiSettings: JSON.stringify(updatedAI),
        updatedAt: new Date().toISOString()
      });
      const { data: updated, errors } = updateResult || {};

      if (!updated) {
        throw new Error(
          'UserPreferences.update returned null' +
          (errors?.length ? `: ${errors.map(e => e.message).join('; ')}` : '')
        );
      }

      // Clear cache
      this.clearCache();

      return {
        ...updated,
        aiSettings: JSON.parse(updated.aiSettings)
      };
    } catch (error) {
      this.logger.error('Error updating AI settings:', error);
      return createNetworkError('Failed to update AI settings', error);
    }
  }

  /**
   * Get specific preference value
   * @param {string} category - Category (sort|ui|ai)
   * @param {string} key - Preference key
   * @param {*} defaultValue - Default value if not found
   * @returns {Promise<*>} Preference value
   */
  async getPreference(category, key, defaultValue = null) {
    try {
      const prefs = await this.getPreferences();
      const categoryPrefs = prefs[`${category}Preferences`] || prefs[`${category}Settings`] || {};
      return categoryPrefs[key] !== undefined ? categoryPrefs[key] : defaultValue;
    } catch (error) {
      this.logger.error('Error getting preference:', error);
      return defaultValue;
    }
  }

  /**
   * Set specific preference value
   * @param {string} category - Category (sort|ui|ai)
   * @param {string} key - Preference key
   * @param {*} value - Preference value
   * @returns {Promise<Object>} Updated preferences
   */
  async setPreference(category, key, value) {
    try {
      const updateObj = { [key]: value };

      if (category === 'sort') {
        return await this.updateSortPreferences(updateObj);
      } else if (category === 'ui') {
        return await this.updateUIPreferences(updateObj);
      } else if (category === 'ai') {
        return await this.updateAISettings(updateObj);
      } else {
        createValidationError(`Unknown preference category: ${category}`);
      }
    } catch (error) {
      this.logger.error('Error setting preference:', error);
      // Don't wrap validation errors - re-throw them
      if (error.name === 'ServiceError') {
        throw error;
      }
      throw createNetworkError('Failed to set preference', error);
    }
  }

  /**
   * Generic update of UserPreferences (Sprint 3 #72 - onboarding wizard)
   * Accepts a partial set of top-level UserPreferences fields and persists them.
   * Automatically serializes known JSON fields (sortPreferences, filterPreferences, viewPreferences).
   *
   * @param {Object} updates - Partial UserPreferences fields
   * @returns {Promise<Object>} Updated preferences
   */
  async updatePreferences(updates = {}) {
    try {
      const prefs = await this.getPreferences();
      const payload = { id: prefs.id };

      const jsonFields = ['sortPreferences', 'filterPreferences', 'viewPreferences'];
      Object.entries(updates).forEach(([key, value]) => {
        if (value === undefined) return;
        if (jsonFields.includes(key)) {
          // Merge with existing JSON state
          const existing = prefs[key] || {};
          const merged = typeof value === 'object' && value !== null
            ? { ...existing, ...value }
            : value;
          payload[key] = typeof merged === 'string' ? merged : JSON.stringify(merged);
        } else {
          payload[key] = value;
        }
      });

      payload.updatedAt = new Date().toISOString();

      const { data: updated } = await this.client.models.UserPreferences.update(payload);

      // Clear cache so next read is fresh
      this.clearCache();

      return updated;
    } catch (error) {
      this.logger.error('Error updating user preferences:', error);
      return createNetworkError('Failed to update user preferences', error);
    }
  }

  /**
   * Clear cache (useful for testing or forcing refresh)
   */
  clearCache() {
    this.cache = null;
    this.cacheTimestamp = null;
  }
}

// Create singleton instance
// Create singleton instance
const userPreferencesServiceInstance = new UserPreferencesService();

// Check feature flag for singleton behavior
const USE_SINGLETON = import.meta.env?.VITE_USE_USER_PREFERENCES_SINGLETON !== 'false' &&
                      process.env.REACT_APP_USE_USER_PREFERENCES_SINGLETON !== 'false';

// Export based on feature flag
export default USE_SINGLETON ? userPreferencesServiceInstance : UserPreferencesService;

// Always export the class for testing
export { UserPreferencesService };
